"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { fadeInUp } from "@/lib/animations";
import Button from "@/components/ui/Button";
import {
  User,
  Mail,
  Phone,
  Building2,
  Calendar,
  Clock,
  Users,
  FileText,
  ChevronLeft,
  ChevronRight,
  Check,
  Loader2,
  X,
} from "lucide-react";
import eventService, {
  EventRequest,
  EventTypeEnum,
  AudienceTypeEnum,
} from "@/app/services/eventService";

interface EventRequestFormProps {
  onClose: () => void;
  onSuccess: () => void;
}

export default function EventRequestForm({
  onClose,
  onSuccess,
}: EventRequestFormProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showError, setShowError] = useState(false);

  const [formData, setFormData] = useState<EventRequest>({
    full_name: "",
    email: "",
    mobile_number: "",
    company_name: "",
    event_type: EventTypeEnum.CONFERENCE,
    event_name: "",
    event_date_start: "",
    event_date_end: "",
    event_duration_from: "",
    event_duration_to: "",
    expected_attendees: 0,
    audience_type: AudienceTypeEnum.PUBLIC,
    required_services: "",
  });

  const totalSteps = 3;

  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  // Handle input change
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;

    // Special handling for start date change - reset end date if needed
    if (name === "event_date_start") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        // Reset end date if it's before new start date
        event_date_end:
          prev.event_date_end && prev.event_date_end < value
            ? ""
            : prev.event_date_end,
      }));
    }
    // Special handling for start time change - reset end time if on same day
    else if (name === "event_duration_from") {
      setFormData((prev) => {
        const isSameDay = prev.event_date_start === prev.event_date_end;
        return {
          ...prev,
          [name]: value,
          // Reset end time if same day and end time is before new start time
          event_duration_to:
            isSameDay &&
            prev.event_duration_to &&
            prev.event_duration_to <= value
              ? ""
              : prev.event_duration_to,
        };
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: name === "expected_attendees" ? parseInt(value) || 0 : value,
      }));
    }
  };

  // Validate current step
  const validateStep = () => {
    setShowError(false);
    setError("");

    if (currentStep === 1) {
      if (
        !formData.full_name ||
        !formData.email ||
        !formData.mobile_number ||
        !formData.company_name
      ) {
        setError("Please fill in all required fields");
        setShowError(true);
        return false;
      }
      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setError("Please enter a valid email address");
        setShowError(true);
        return false;
      }
    }

    if (currentStep === 2) {
      if (
        !formData.event_type ||
        !formData.event_name ||
        !formData.event_date_start ||
        !formData.event_date_end ||
        !formData.event_duration_from ||
        !formData.event_duration_to
      ) {
        setError("Please fill in all required fields");
        setShowError(true);
        return false;
      }

      // Date validation
      if (formData.event_date_start > formData.event_date_end) {
        setError("End date must be on or after start date");
        setShowError(true);
        return false;
      }

      // Time validation (only if same day)
      if (formData.event_date_start === formData.event_date_end) {
        if (formData.event_duration_from >= formData.event_duration_to) {
          setError("End time must be after start time");
          setShowError(true);
          return false;
        }
      }
    }

    if (currentStep === 3) {
      if (
        !formData.expected_attendees ||
        !formData.audience_type ||
        !formData.required_services
      ) {
        setError("Please fill in all required fields");
        setShowError(true);
        return false;
      }
      if (formData.expected_attendees < 1) {
        setError("Expected attendees must be at least 1");
        setShowError(true);
        return false;
      }
    }

    return true;
  };

  // Next step
  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
    }
  };

  // Previous step
  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    setShowError(false);
  };

  // Submit form
  const handleSubmit = async () => {
    if (!validateStep()) return;

    setLoading(true);
    setShowError(false);
    setError("");

    try {
      const payload = {
        ...formData,
        mobile_number: eventService.formatPhoneNumber(formData.mobile_number),
      };

      await eventService.createEventRequest(payload);
      onSuccess();
    } catch (err: any) {
      console.error("Event request error:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to submit event request. Please try again.",
      );
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const eventTypeOptions = eventService.getEventTypeOptions();
  const audienceTypeOptions = eventService.getAudienceTypeOptions();

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          {[1, 2, 3].map((step) => (
            <div key={step} className="flex items-center flex-1">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                  step < currentStep
                    ? "bg-primary text-white"
                    : step === currentStep
                      ? "bg-primary text-white ring-4 ring-primary/20"
                      : "bg-muted text-text"
                }`}
              >
                {step < currentStep ? <Check className="w-5 h-5" /> : step}
              </div>
              {step < 3 && (
                <div
                  className={`flex-1 h-1 mx-2 rounded transition-all ${
                    step < currentStep ? "bg-primary" : "bg-muted"
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between text-xs sm:text-sm text-text/60">
          <span>Personal Info</span>
          <span>Event Details</span>
          <span>Additional Info</span>
        </div>
      </div>

      {/* Error Alert */}
      <AnimatePresence>
        {showError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-6 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4"
          >
            <div className="flex items-start gap-3">
              <X className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-700 dark:text-red-300">
                  {error}
                </p>
              </div>
              <button
                onClick={() => setShowError(false)}
                className="text-red-600 hover:text-red-800"
              >
                ×
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Form Steps */}
      <AnimatePresence mode="wait">
        {/* Step 1: Personal Info */}
        {currentStep === 1 && (
          <motion.div
            key="step1"
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="space-y-5"
          >
            <div>
              <label className="block text-text text-sm font-medium mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text/50" />
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  className="w-full pl-11 pr-4 py-3 bg-card/50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-heading transition-all"
                  placeholder="Enter your full name"
                />
              </div>
            </div>

            <div>
              <label className="block text-text text-sm font-medium mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text/50" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full pl-11 pr-4 py-3 bg-card/50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-heading transition-all"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-text text-sm font-medium mb-2">
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text/50" />
                <input
                  type="tel"
                  name="mobile_number"
                  value={formData.mobile_number}
                  onChange={handleChange}
                  required
                  className="w-full pl-11 pr-4 py-3 bg-card/50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-heading transition-all"
                  placeholder="+966 5X XXX XXXX"
                />
              </div>
              <p className="text-xs text-text/60 mt-1">
                Saudi format: +966 or 05XXXXXXXX
              </p>
            </div>

            <div>
              <label className="block text-text text-sm font-medium mb-2">
                Company Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text/50" />
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  className="w-full pl-11 pr-4 py-3 bg-card/50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-heading transition-all"
                  placeholder="Your company name"
                />
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 2: Event Details */}
        {currentStep === 2 && (
          <motion.div
            key="step2"
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="space-y-5"
          >
            <div>
              <label className="block text-text text-sm font-medium mb-2">
                Event Type <span className="text-red-500">*</span>
              </label>
              <select
                name="event_type"
                value={formData.event_type}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-card/50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-heading transition-all"
              >
                {eventTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-text text-sm font-medium mb-2">
                Event Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="event_name"
                value={formData.event_name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-card/50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-heading transition-all"
                placeholder="e.g., Tech Summit 2026"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-text text-sm font-medium mb-2">
                  Start Date <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text/50 pointer-events-none" />
                  <input
                    type="date"
                    name="event_date_start"
                    value={formData.event_date_start}
                    onChange={handleChange}
                    required
                    min={getMinDate()}
                    className="w-full pl-11 pr-4 py-3 bg-card/50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-heading transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-text text-sm font-medium mb-2">
                  End Date <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text/50 pointer-events-none" />
                  <input
                    type="date"
                    name="event_date_end"
                    value={formData.event_date_end}
                    onChange={handleChange}
                    required
                    min={formData.event_date_start || getMinDate()}
                    disabled={!formData.event_date_start}
                    className="w-full pl-11 pr-4 py-3 bg-card/50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-heading transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
                {!formData.event_date_start && (
                  <p className="text-xs text-text/60 mt-1">
                    Select start date first
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-text text-sm font-medium mb-2">
                  Start Time <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text/50 pointer-events-none" />
                  <input
                    type="time"
                    name="event_duration_from"
                    value={formData.event_duration_from}
                    onChange={handleChange}
                    required
                    className="w-full pl-11 pr-4 py-3 bg-card/50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-heading transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-text text-sm font-medium mb-2">
                  End Time <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text/50 pointer-events-none" />
                  <input
                    type="time"
                    name="event_duration_to"
                    value={formData.event_duration_to}
                    onChange={handleChange}
                    required
                    min={
                      formData.event_date_start === formData.event_date_end
                        ? formData.event_duration_from
                        : undefined
                    }
                    className="w-full pl-11 pr-4 py-3 bg-card/50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-heading transition-all"
                  />
                </div>
                {formData.event_date_start === formData.event_date_end &&
                  formData.event_duration_from && (
                    <p className="text-xs text-text/60 mt-1">
                      Must be after {formData.event_duration_from}
                    </p>
                  )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 3: Additional Info */}
        {currentStep === 3 && (
          <motion.div
            key="step3"
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="space-y-5"
          >
            <div>
              <label className="block text-text text-sm font-medium mb-2">
                Expected Attendees <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text/50" />
                <input
                  type="number"
                  name="expected_attendees"
                  value={formData.expected_attendees || ""}
                  onChange={handleChange}
                  required
                  min="1"
                  className="w-full pl-11 pr-4 py-3 bg-card/50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-heading transition-all"
                  placeholder="Number of attendees"
                />
              </div>
            </div>

            <div>
              <label className="block text-text text-sm font-medium mb-2">
                Audience Type <span className="text-red-500">*</span>
              </label>
              <select
                name="audience_type"
                value={formData.audience_type}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-card/50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-heading transition-all"
              >
                {audienceTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-text text-sm font-medium mb-2">
                Required Services <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-3 w-5 h-5 text-text/50" />
                <textarea
                  name="required_services"
                  value={formData.required_services}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="w-full pl-11 pr-4 py-3 bg-card/50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-heading resize-none transition-all"
                  placeholder="e.g., Catering, AV equipment, parking, WiFi, stage setup..."
                />
              </div>
              <p className="text-xs text-text/60 mt-1">
                Please describe all services you'll need for your event
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
        <Button
          variant="outline"
          onClick={currentStep === 1 ? onClose : handlePrevious}
          disabled={loading}
          className="gap-2"
        >
          <ChevronLeft className="w-4 h-4" />
          {currentStep === 1 ? "Cancel" : "Previous"}
        </Button>

        {currentStep < totalSteps ? (
          <Button
            variant="primary"
            onClick={handleNext}
            disabled={loading}
            className="gap-2"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={loading}
            className="gap-2 min-w-[120px]"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                Submit
                <Check className="w-4 h-4" />
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
